import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import Button from '@atlaskit/button'
import TextField from '@atlaskit/textfield'
import Select from '@atlaskit/select'
import Lozenge from '@atlaskit/lozenge'
import Spinner from '@atlaskit/spinner'
import Tooltip from '@atlaskit/tooltip'
import SearchIcon from '@atlaskit/icon/glyph/search'
import FolderIcon from '@atlaskit/icon/glyph/folder'
import FolderFilledIcon from '@atlaskit/icon/glyph/folder-filled'
import DocumentsIcon from '@atlaskit/icon/glyph/documents'
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right'
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down'
import InfoIcon from '@atlaskit/icon/glyph/info'
import CrossIcon from '@atlaskit/icon/glyph/cross'
import { fetchActiveReleases } from '../../api/releaseApi'
import {
  fetchFolderTree,
  fetchAllScenariosInRelease,
  fetchScenariosInFolder
} from '../../api/folderApi'
import { bulkLinkScenarios } from '../../api/issueLinkApi'
import { isJiraConnect, getJiraContext } from '../../utils/jiraContext'
import { useLicense } from '../../contexts/LicenseContext'
import LicenseRequiredPage from '../common/LicenseRequiredPage'
import { setActiveProjectId, DEFAULT_JIRA_PROJECT_ID } from '../../constants/config'

/**
 * LINK SCENARIOS PAGE
 *
 * Standalone page rendered inside a Jira AP.dialog.
 * This is NOT wrapped in a Dialog component — the Jira dialog chrome provides the frame.
 *
 * Gets context from AP.dialog.getCustomData() (jiraIssueKey, jiraProjectId, alreadyLinkedIds).
 * On successful link, calls AP.dialog.close({ linked: true }) to notify the opener.
 *
 * The selection UI (release picker, folder tree, scenario list) is the same pattern
 * as ImportFromReleaseModal / LinkScenariosModal.
 */
function LinkScenariosPage() {
  const dispatch = useDispatch()
  const { isLicenseValid, licenseMessage } = useLicense()

  // Context from AP.dialog custom data
  const [jiraIssueKey, setJiraIssueKey] = useState(null)
  const [jiraProjectId, setJiraProjectId] = useState(null)
  const [alreadyLinkedIds, setAlreadyLinkedIds] = useState(new Set())
  const [contextLoaded, setContextLoaded] = useState(false)

  // Release picker state
  const [availableReleases, setAvailableReleases] = useState([])
  const [sourceReleaseId, setSourceReleaseId] = useState(null)
  const [releasesLoading, setReleasesLoading] = useState(false)

  // Source release data state
  const [sourceFolderTree, setSourceFolderTree] = useState([])
  const [sourceScenarios, setSourceScenarios] = useState([])
  const [allSourceScenarios, setAllSourceScenarios] = useState([])

  // Navigation and selection state
  const [localSelectedFolderId, setLocalSelectedFolderId] = useState(null)
  const [expandedFolderIds, setExpandedFolderIds] = useState([])
  const [selectedScenarioIds, setSelectedScenarioIds] = useState(new Set())
  const [searchQuery, setSearchQuery] = useState('')

  // Loading and progress state
  const [foldersLoading, setFoldersLoading] = useState(false)
  const [scenariosLoading, setScenariosLoading] = useState(false)
  const [linkInProgress, setLinkInProgress] = useState(false)

  // Determine which scenarios to display (folder-specific or all, with search filter)
  const displayScenarios = useMemo(() => {
    const baseScenarios = localSelectedFolderId !== null ? sourceScenarios : allSourceScenarios

    if (!searchQuery.trim()) return baseScenarios

    const query = searchQuery.toLowerCase()
    return baseScenarios.filter(s =>
      s.scenarioName?.toLowerCase().includes(query) ||
      s.description?.toLowerCase().includes(query) ||
      s.scenarioKey?.toLowerCase().includes(query)
    )
  }, [localSelectedFolderId, sourceScenarios, allSourceScenarios, searchQuery])

  // Filter out already-linked scenarios for "select all" count
  const selectableScenarios = useMemo(() => {
    return displayScenarios.filter(s => !alreadyLinkedIds.has(s.id))
  }, [displayScenarios, alreadyLinkedIds])

  /**
   * INITIALIZE - Get custom data from AP.dialog or Jira context
   */
  useEffect(() => {
    const init = async () => {
      try {
        if (isJiraConnect() && window.AP?.dialog?.getCustomData) {
          // Get custom data passed from IssuePanel via AP.dialog.create()
          window.AP.dialog.getCustomData((customData) => {
            console.log('[LinkScenariosPage] Custom data:', customData)
            if (customData) {
              setJiraIssueKey(customData.jiraIssueKey)
              setJiraProjectId(customData.jiraProjectId)
              if (customData.alreadyLinkedIds) {
                setAlreadyLinkedIds(new Set(customData.alreadyLinkedIds))
              }
              if (customData.jiraProjectId) {
                setActiveProjectId(customData.jiraProjectId)
              }
            }
            setContextLoaded(true)
          })
        } else {
          // Dev mode fallback
          setJiraIssueKey('DEMO-1')
          setJiraProjectId(DEFAULT_JIRA_PROJECT_ID)
          setContextLoaded(true)
        }
      } catch (error) {
        console.error('Failed to get dialog custom data:', error)
        // Fallback: try to get context from AP context
        try {
          const context = await getJiraContext()
          setJiraIssueKey(context?.jira?.issue?.key || 'DEMO-1')
          setJiraProjectId(context?.jira?.project?.id || DEFAULT_JIRA_PROJECT_ID)
        } catch (e) {
          console.error('Failed to get Jira context:', e)
          setJiraIssueKey('DEMO-1')
          setJiraProjectId(DEFAULT_JIRA_PROJECT_ID)
        }
        setContextLoaded(true)
      }
    }
    init()
  }, [])

  // Fetch available releases once context is loaded
  useEffect(() => {
    if (contextLoaded && jiraProjectId) {
      setReleasesLoading(true)
      fetchActiveReleases(jiraProjectId)
        .then(releases => {
          setAvailableReleases(releases || [])
        })
        .catch(err => {
          console.error('Failed to fetch releases:', err)
        })
        .finally(() => {
          setReleasesLoading(false)
        })
    }
  }, [contextLoaded, jiraProjectId])

  /**
   * HANDLE RELEASE SELECT
   */
  const handleReleaseSelect = async (releaseId) => {
    setSourceReleaseId(releaseId)
    setLocalSelectedFolderId(null)
    setSelectedScenarioIds(new Set())
    setSearchQuery('')
    setExpandedFolderIds([])

    if (!releaseId) {
      setSourceFolderTree([])
      setSourceScenarios([])
      setAllSourceScenarios([])
      return
    }

    setFoldersLoading(true)
    setScenariosLoading(true)

    try {
      const [tree, scenarios] = await Promise.all([
        fetchFolderTree(releaseId),
        fetchAllScenariosInRelease(releaseId)
      ])

      setSourceFolderTree(tree || [])
      setAllSourceScenarios(scenarios || [])

      if (tree && tree.length > 0) {
        const firstFolderId = tree[0].id
        setLocalSelectedFolderId(firstFolderId)
        try {
          const folderScenarios = await fetchScenariosInFolder(firstFolderId)
          setSourceScenarios(folderScenarios || [])
        } catch (err) {
          console.error('Failed to fetch first folder scenarios:', err)
          setSourceScenarios([])
        }
      } else {
        setSourceScenarios(scenarios || [])
      }
    } catch (error) {
      console.error('Failed to fetch release data:', error)
    } finally {
      setFoldersLoading(false)
      setScenariosLoading(false)
    }
  }

  /**
   * HANDLE FOLDER SELECT
   */
  const handleFolderSelect = async (folderId) => {
    setLocalSelectedFolderId(folderId)
    setSelectedScenarioIds(new Set())

    if (folderId !== null) {
      setScenariosLoading(true)
      try {
        const scenarios = await fetchScenariosInFolder(folderId)
        setSourceScenarios(scenarios || [])
      } catch (error) {
        console.error('Failed to fetch folder scenarios:', error)
        setSourceScenarios([])
      } finally {
        setScenariosLoading(false)
      }
    }
  }

  /**
   * TOGGLE FOLDER EXPAND
   */
  const toggleFolderExpand = (folderId) => {
    setExpandedFolderIds(prev => {
      if (prev.includes(folderId)) {
        return prev.filter(id => id !== folderId)
      }
      return [...prev, folderId]
    })
  }

  /**
   * HANDLE SELECT ALL
   */
  const handleSelectAll = () => {
    if (selectedScenarioIds.size === selectableScenarios.length) {
      setSelectedScenarioIds(new Set())
    } else {
      setSelectedScenarioIds(new Set(selectableScenarios.map(s => s.id)))
    }
  }

  /**
   * HANDLE SCENARIO TOGGLE
   */
  const handleScenarioToggle = (scenarioId) => {
    if (alreadyLinkedIds.has(scenarioId)) return

    setSelectedScenarioIds(prev => {
      const next = new Set(prev)
      if (next.has(scenarioId)) {
        next.delete(scenarioId)
      } else {
        next.add(scenarioId)
      }
      return next
    })
  }

  /**
   * HANDLE LINK SELECTED
   * Links selected scenarios then closes the dialog with result data.
   */
  const handleLinkSelected = async () => {
    if (selectedScenarioIds.size === 0) return

    setLinkInProgress(true)
    try {
      await bulkLinkScenarios({
        testScenarioIds: Array.from(selectedScenarioIds),
        jiraIssueKey
      })

      // Close the Jira dialog and pass result back to the opener (IssuePanel)
      if (isJiraConnect() && window.AP?.dialog?.close) {
        window.AP.dialog.close({ linked: true, count: selectedScenarioIds.size })
      }
    } catch (error) {
      console.error('Failed to link scenarios:', error)
      setLinkInProgress(false)
    }
  }

  /**
   * HANDLE CANCEL
   */
  const handleCancel = () => {
    if (isJiraConnect() && window.AP?.dialog?.close) {
      window.AP.dialog.close({ linked: false })
    }
  }

  /**
   * GET PRIORITY APPEARANCE
   */
  const getPriorityAppearance = (priority) => {
    const map = {
      'Critical': 'removed',
      'High': 'moved',
      'Medium': 'inprogress',
      'Low': 'default'
    }
    return map[priority] || 'default'
  }

  /**
   * RENDER FOLDER TREE ITEM
   */
  const renderFolderItem = (folder, depth = 0) => {
    const hasChildren = folder.children && folder.children.length > 0
    const isExpanded = expandedFolderIds.includes(folder.id)
    const isSelected = localSelectedFolderId === folder.id
    const scenarioCount = folder.testScenarioCount || folder.scenarioCount || 0

    return (
      <div key={folder.id}>
        <div
          className={`library-folder-item ${isSelected ? 'selected' : ''}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '6px 8px',
            paddingLeft: `${8 + depth * 16}px`,
            cursor: 'pointer',
            borderRadius: '4px',
            backgroundColor: isSelected ? 'var(--ds-background-selected, #E9F2FF)' : 'transparent',
            marginBottom: '2px'
          }}
          onClick={() => handleFolderSelect(folder.id)}
        >
          {/* Expand/Collapse chevron */}
          <span
            style={{
              width: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              visibility: hasChildren ? 'visible' : 'hidden'
            }}
            onClick={(e) => {
              e.stopPropagation()
              if (hasChildren) {
                toggleFolderExpand(folder.id)
              }
            }}
          >
            {isExpanded
              ? <ChevronDownIcon label="Collapse" size="small" />
              : <ChevronRightIcon label="Expand" size="small" />
            }
          </span>

          {/* Folder icon */}
          <span style={{ marginRight: '6px', display: 'flex', alignItems: 'center' }}>
            {isSelected
              ? <FolderFilledIcon label="Folder" size="small" primaryColor="var(--ds-icon-brand, #0052CC)" />
              : <FolderIcon label="Folder" size="small" />
            }
          </span>

          {/* Folder name */}
          <span style={{
            flex: 1,
            fontSize: '13px',
            color: isSelected ? 'var(--ds-text-selected, #0052CC)' : 'var(--ds-text, #172B4D)',
            fontWeight: isSelected ? 500 : 400,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {folder.name}
          </span>

          {/* Scenario count badge */}
          {scenarioCount > 0 && (
            <span style={{
              fontSize: '11px',
              color: 'var(--ds-text-subtlest, #6B778C)',
              backgroundColor: 'var(--ds-background-neutral, #F4F5F7)',
              padding: '2px 6px',
              borderRadius: '10px'
            }}>
              {scenarioCount}
            </span>
          )}
        </div>

        {/* Children folders */}
        {hasChildren && isExpanded && (
          <div className="folder-children">
            {folder.children.map(child => renderFolderItem(child, depth + 1))}
          </div>
        )}
      </div>
    )
  }

  // Check if all selectable scenarios are selected
  const allSelected = selectableScenarios.length > 0 && selectedScenarioIds.size === selectableScenarios.length

  // Build release dropdown options
  const releaseOptions = availableReleases.map(r => ({
    value: r.id,
    label: r.name
  }))

  const selectedReleaseOption = releaseOptions.find(o => o.value === sourceReleaseId) || null

  // License validation gate - show compact license page in dialog
  if (!isLicenseValid) {
    return <LicenseRequiredPage message={licenseMessage} compact />
  }

  // Show loading until context is available
  if (!contextLoaded) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '40px' }}>
        <Spinner size="large" />
      </div>
    )
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif'
    }}>
      {/* Dialog Header — title + close button */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid var(--ds-border, #DFE1E6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: 500,
          color: 'var(--ds-text, #172B4D)',
          margin: 0
        }}>
          Link Test Scenarios
        </h2>
        <Button
          appearance="subtle"
          spacing="compact"
          iconBefore={<CrossIcon label="Close" size="medium" />}
          onClick={handleCancel}
        />
      </div>

      {/* Info bar */}
      <div style={{
        padding: '12px 20px',
        borderBottom: '1px solid var(--ds-border, #DFE1E6)',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '13px',
        color: 'var(--ds-text-subtlest, #6B778C)'
      }}>
        <InfoIcon label="Info" size="small" primaryColor="var(--ds-text-subtlest, #6B778C)" />
        Select test scenarios to link to <strong style={{ margin: '0 3px' }}>{jiraIssueKey}</strong>. Linked scenarios will appear on this issue.
      </div>

      {/* Release Selector */}
      <div style={{
        padding: '12px 20px',
        borderBottom: '1px solid var(--ds-border, #DFE1E6)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <span style={{
          fontSize: '13px',
          fontWeight: 500,
          color: 'var(--ds-text, #172B4D)',
          whiteSpace: 'nowrap'
        }}>
          Release:
        </span>
        <div style={{ flex: 1, maxWidth: '400px' }}>
          <Select
            options={releaseOptions}
            value={selectedReleaseOption}
            onChange={(option) => handleReleaseSelect(option?.value || null)}
            placeholder="Select a release..."
            isLoading={releasesLoading}
            isClearable
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 1100 })
            }}
          />
        </div>
      </div>

      {/* Body - Two Column Layout */}
      <div style={{
        flex: 1,
        display: 'flex',
        overflow: 'hidden'
      }}>
        {/* Left Panel - Folders */}
        <div style={{
          width: '260px',
          borderRight: '1px solid var(--ds-border, #DFE1E6)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Folder Panel Header */}
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--ds-border, #DFE1E6)'
          }}>
            <h3 style={{
              fontSize: '12px',
              fontWeight: 600,
              color: 'var(--ds-text-subtlest, #6B778C)',
              margin: 0,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Folders
            </h3>
          </div>

          {/* Folder Tree */}
          <div style={{
            flex: 1,
            overflow: 'auto',
            padding: '8px'
          }}>
            {!sourceReleaseId ? (
              <p style={{
                fontSize: '13px',
                color: 'var(--ds-text-subtlest, #6B778C)',
                textAlign: 'center',
                padding: '20px'
              }}>
                Select a release above to browse its folders and scenarios.
              </p>
            ) : foldersLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <Spinner size="small" />
              </div>
            ) : sourceFolderTree.length > 0 ? (
              <>
                {sourceFolderTree.map(folder => renderFolderItem(folder))}
              </>
            ) : (
              <p style={{
                fontSize: '13px',
                color: 'var(--ds-text-subtlest, #6B778C)',
                textAlign: 'center',
                padding: '12px'
              }}>
                No folders in this release.
              </p>
            )}
          </div>
        </div>

        {/* Right Panel - Scenarios */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Search and Select All Row */}
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--ds-border, #DFE1E6)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{ flex: 1 }}>
              <TextField
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search scenarios..."
                elemBeforeInput={
                  <div style={{ paddingLeft: '8px', display: 'flex', alignItems: 'center' }}>
                    <SearchIcon label="Search" size="small" primaryColor="var(--ds-text-subtlest, #6B778C)" />
                  </div>
                }
                isCompact
                isDisabled={!sourceReleaseId}
              />
            </div>

            {selectableScenarios.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) {
                      el.indeterminate = selectedScenarioIds.size > 0 && !allSelected
                    }
                  }}
                  onChange={handleSelectAll}
                  style={{
                    width: '16px',
                    height: '16px',
                    cursor: 'pointer',
                    accentColor: 'var(--ds-background-brand-bold, #0052CC)'
                  }}
                />
                <span style={{
                  fontSize: '13px',
                  color: 'var(--ds-text-subtlest, #6B778C)'
                }}>
                  Select all ({selectableScenarios.length})
                </span>
              </div>
            )}
          </div>

          {/* Scenario List */}
          <div style={{
            flex: 1,
            overflow: 'auto',
            padding: '12px 16px'
          }}>
            {!sourceReleaseId ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px',
                textAlign: 'center',
                height: '100%'
              }}>
                <DocumentsIcon
                  label="Select a release"
                  size="large"
                  primaryColor="var(--ds-text-subtlest, #6B778C)"
                />
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'var(--ds-text, #172B4D)',
                  margin: '16px 0 8px'
                }}>
                  Select a release
                </h3>
                <p style={{
                  fontSize: '13px',
                  color: 'var(--ds-text-subtlest, #6B778C)',
                  margin: 0
                }}>
                  Choose a release from the dropdown above to browse its scenarios.
                </p>
              </div>
            ) : scenariosLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <Spinner size="medium" />
              </div>
            ) : displayScenarios.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {displayScenarios.map(scenario => {
                  const isAlreadyLinked = alreadyLinkedIds.has(scenario.id)
                  const isSelected = selectedScenarioIds.has(scenario.id)

                  return (
                    <div
                      key={scenario.id}
                      className="import-scenario-item"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 12px',
                        borderRadius: '4px',
                        border: `1px solid ${isSelected ? 'var(--ds-border-selected, #4C9AFF)' : 'var(--ds-border, #DFE1E6)'}`,
                        backgroundColor: isSelected ? 'var(--ds-background-selected, #E9F2FF)' : isAlreadyLinked ? 'var(--ds-background-disabled, #F4F5F7)' : 'var(--ds-surface, #FFFFFF)',
                        cursor: isAlreadyLinked ? 'default' : 'pointer',
                        transition: 'all 0.15s ease',
                        opacity: isAlreadyLinked ? 0.7 : 1
                      }}
                      onClick={() => handleScenarioToggle(scenario.id)}
                    >
                      {/* Checkbox */}
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ marginRight: '12px' }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isAlreadyLinked}
                          onChange={() => handleScenarioToggle(scenario.id)}
                          style={{
                            width: '16px',
                            height: '16px',
                            cursor: isAlreadyLinked ? 'not-allowed' : 'pointer',
                            accentColor: 'var(--ds-background-brand-bold, #0052CC)'
                          }}
                        />
                      </div>

                      {/* Scenario Info */}
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            fontSize: '12px',
                            color: 'var(--ds-text-subtlest, #6B778C)',
                            fontFamily: 'monospace'
                          }}>
                            {scenario.scenarioKey}
                          </span>
                          <span style={{
                            fontSize: '13px',
                            fontWeight: 500,
                            color: 'var(--ds-text, #172B4D)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {scenario.scenarioName}
                          </span>
                        </div>
                        {scenario.description && (
                          <p style={{
                            fontSize: '12px',
                            color: 'var(--ds-text-subtlest, #6B778C)',
                            margin: '4px 0 0',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {scenario.description}
                          </p>
                        )}
                      </div>

                      {/* Metadata */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '12px' }}>
                        {isAlreadyLinked && (
                          <Lozenge appearance="success">Linked</Lozenge>
                        )}
                        {scenario.testCaseCount > 0 && (
                          <Tooltip content={`${scenario.testCaseCount} test case(s)`}>
                            <span style={{
                              fontSize: '11px',
                              color: 'var(--ds-text-subtlest, #6B778C)',
                              backgroundColor: 'var(--ds-background-neutral, #F4F5F7)',
                              padding: '2px 6px',
                              borderRadius: '3px'
                            }}>
                              {scenario.testCaseCount} {scenario.testCaseCount === 1 ? 'case' : 'cases'}
                            </span>
                          </Tooltip>
                        )}
                        {scenario.priority && (
                          <Lozenge appearance={getPriorityAppearance(scenario.priority)}>
                            {scenario.priority}
                          </Lozenge>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px',
                textAlign: 'center',
                height: '100%'
              }}>
                <DocumentsIcon
                  label="No scenarios"
                  size="large"
                  primaryColor="var(--ds-text-subtlest, #6B778C)"
                />
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'var(--ds-text, #172B4D)',
                  margin: '16px 0 8px'
                }}>
                  {searchQuery ? 'No matching scenarios' : 'No scenarios in this release'}
                </h3>
                <p style={{
                  fontSize: '13px',
                  color: 'var(--ds-text-subtlest, #6B778C)',
                  margin: 0
                }}>
                  {searchQuery
                    ? 'Try a different search term.'
                    : 'This release has no scenarios to link.'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        padding: '12px 20px',
        borderTop: '1px solid var(--ds-border, #DFE1E6)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {/* Selection count */}
        <span style={{
          fontSize: '13px',
          color: 'var(--ds-text-subtlest, #6B778C)'
        }}>
          {selectedScenarioIds.size > 0
            ? `${selectedScenarioIds.size} scenario(s) selected`
            : 'Select scenarios to link'}
        </span>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            appearance="subtle"
            onClick={handleCancel}
            isDisabled={linkInProgress}
          >
            Cancel
          </Button>
          <Button
            appearance="primary"
            onClick={handleLinkSelected}
            isLoading={linkInProgress}
            isDisabled={selectedScenarioIds.size === 0 || !sourceReleaseId}
          >
            Link {selectedScenarioIds.size > 0 ? `(${selectedScenarioIds.size})` : ''}
          </Button>
        </div>
      </div>
    </div>
  )
}

export { LinkScenariosPage }
